import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { createResponse, type BodyType } from '@/api/responses'
import { createScenario } from '@/api/scenarios'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ── Schema ─────────────────────────────────────────────────────────────────

const BODY_TYPES: BodyType[] = ['json', 'text', 'xml', 'binary_base64']

const schema = z.object({
  scenarioName: z.string().min(1).max(100),
  priority: z.coerce.number().int().default(0),
  enabled: z.boolean().default(true),
  status: z.coerce.number().int().min(100).max(599).default(200),
  body_type: z.enum(['json', 'text', 'xml', 'binary_base64'] as const).default('json'),
  body: z.string().default(''),
  headersJson: z
    .string()
    .default('{}')
    .refine((s) => {
      try { JSON.parse(s); return true } catch { return false }
    }),
  delay_ms: z.coerce.number().int().min(0).max(30000).default(0),
})

type FormValues = z.infer<typeof schema>

// ── Component ──────────────────────────────────────────────────────────────

interface StaticScenarioFormProps {
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

export function StaticScenarioForm({ projectId, endpointId, onSuccess, onCancel }: StaticScenarioFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { register, handleSubmit, control, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        scenarioName: '',
        priority: 0,
        enabled: true,
        status: 200,
        body_type: 'json',
        body: '{\n  \n}',
        headersJson: '{}',
        delay_ms: 0,
      },
    })

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const headers = JSON.parse(data.headersJson) as Record<string, string>
      const response = await createResponse(projectId, {
        status: data.status,
        body_type: data.body_type,
        body: data.body || undefined,
        headers,
        delay_ms: data.delay_ms,
      })
      await createScenario(endpointId, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        type: 'static',
        config: { response_id: response.id },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('scenarioForm.createFailed'))
    },
  })

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {serverError}
        </p>
      )}

      {/* ── Scenario settings ─────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="scenarioName">{t('scenarioForm.scenarioName')}</Label>
            <Input id="scenarioName" placeholder={t('scenarioForm.scenarioNamePlaceholder')} {...register('scenarioName')} />
            {errors.scenarioName && (
              <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="priority">{t('scenarioForm.priority')}</Label>
            <Input id="priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="mt-1"
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* ── Response settings ────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.responseSettings')}</h4>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="status">{t('scenarioForm.statusCode')}</Label>
            <Input id="status" type="number" {...register('status')} />
            {errors.status && (
              <p className="text-sm text-destructive">{errors.status.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t('scenarioForm.bodyType')}</Label>
            <Controller
              name="body_type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BODY_TYPES.map((bt) => (
                      <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="delay_ms">{t('scenarioForm.delayMs')}</Label>
            <Input id="delay_ms" type="number" {...register('delay_ms')} />
            {errors.delay_ms && (
              <p className="text-sm text-destructive">{errors.delay_ms.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="body">{t('scenarioForm.body')}</Label>
          <Textarea
            id="body"
            rows={6}
            className="font-mono text-sm"
            placeholder='{"message": "ok"}'
            {...register('body')}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="headersJson">{t('scenarioForm.headers')}</Label>
          <Textarea
            id="headersJson"
            rows={3}
            className="font-mono text-sm"
            placeholder='{"Content-Type": "application/json"}'
            {...register('headersJson')}
          />
          {errors.headersJson && (
            <p className="text-sm text-destructive">{t('scenarioForm.headersJsonError')}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? t('common.creating') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
